import React from 'react';
import { Helmet } from 'react-helmet-async';

// Helper function to generate structured data based on page type
const getStructuredData = (siteUrl: string, description: string, schemaType: string, product?: any) => {
  const baseOrganization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Skytion Enterprises',
    alternateName: ['Skytion.in', 'Skysun Brand'],
    url: siteUrl,
    logo: `${siteUrl}/assets/skytion/brand/skytion logo png.png`,
    description: 'Premium tissue manufacturing company established in 2022. Serving India, USA, Indonesia with 9 product lines from our Noida facility.',
    foundingDate: '2022',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+91-8920065965',
      contactType: 'customer service',
      email: 'sales@skytion.in',
      availableLanguage: ['English', 'Hindi']
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Ecotech Phase 3',
      addressLocality: 'Gautam Buddha Nagar',
      addressRegion: 'Uttar Pradesh',
      addressCountry: 'IN',
      addressCountryText: 'India'
    },
    areaServed: [
      {
        '@type': 'Country',
        name: 'India'
      },
      {
        '@type': 'Country', 
        name: 'United States'
      },
      {
        '@type': 'Country',
        name: 'Indonesia'
      }
    ],
    makesOffer: {
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Product',
        name: 'Tissue Products',
        description: 'Premium tissue products including toilet rolls, kitchen towels, dining tissues, C-fold, M-fold tissues'
      }
    },
    sameAs: []
  };

  switch (schemaType) {
    case 'Product':
      if (product) {
        return {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: product.name,
          description: product.description,
          brand: {
            '@type': 'Brand',
            name: product.brand
          },
          manufacturer: {
            '@type': 'Organization',
            name: product.manufacturer,
            url: siteUrl
          },
          image: `${siteUrl}${product.image}`,
          category: product.category,
          offers: {
            '@type': 'Offer',
            availability: 'https://schema.org/InStock',
            seller: {
              '@type': 'Organization',
              name: 'Skytion Enterprises'
            }
          }
        };
      }
      return baseOrganization;
    
    case 'ContactPage':
      return {
        ...baseOrganization,
        '@type': 'ContactPage',
        mainEntity: baseOrganization
      };
    
    case 'AboutPage':
      return {
        ...baseOrganization,
        '@type': 'AboutPage',
        mainEntity: baseOrganization
      };
    
    default:
      return {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: description,
        description: description,
        url: siteUrl,
        isPartOf: {
          '@type': 'WebSite',
          name: 'Skytion.in',
          url: siteUrl
        },
        about: baseOrganization,
        mainEntity: baseOrganization
      };
  }
};

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  canonical?: string;
  schemaType?: 'WebPage' | 'Product' | 'Organization' | 'ContactPage' | 'AboutPage';
  product?: {
    name: string;
    description: string;
    brand: string;
    manufacturer: string;
    image: string;
    category: string;
  };
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  ogImage = '/favicon-512x512.png',
  canonical,
  schemaType = 'WebPage',
  product,
}) => {
  const siteUrl = 'https://skytion.in';
  const fullTitle = `${title} | Skytion.in - Tissue Manufacturing & Import Export`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Additional SEO Meta Tags */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="author" content="Skytion Enterprises" />
      <meta name="language" content="en" />
      <meta name="geo.region" content="IN-UP" />
      <meta name="geo.placename" content="Noida" />
      <meta name="geo.position" content="28.5355;77.3910" />
      <meta name="ICBM" content="28.5355, 77.3910" />
      
      {/* Mobile Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=no, maximum-scale=1" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      
      {/* Theme Colors */}
      <meta name="theme-color" content="#14b8a6" />
      <meta name="msapplication-TileColor" content="#14b8a6" />
      
      {/* Open Graph / Social Media */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`${siteUrl}${ogImage}`} />
      <meta property="og:url" content={canonical || window.location.href} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${siteUrl}${ogImage}`} />
      
      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}
      
      {/* Enhanced Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(getStructuredData(siteUrl, description, schemaType, product))}
      </script>
    </Helmet>
  );
};

export default SEO;
